import React, { useEffect, useState } from 'react';
import back from '../dist/webImages/back.svg';
import { Field, Form, Formik } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import swal from 'sweetalert';
import { toast } from 'react-toastify';
import FormControl from '../components/form/FormControl';
import SubmitButton from '../components/SubmitButton';
import OneImageUpload from '../components/OneImageUpload';
import usePost2 from '../customHooks/usePost2';

const canAccessGuideAction = (permission, action) =>
  permission?.Guidance?.includes(action) ||
  permission?.Guide?.includes(action);

const GuidanceCreate = ({ permission }) => {
  const navigate = useNavigate();
  const [res, apiMethod] = usePost2();
  const [datas, setDatas] = useState({
    guide_image: '',
  });
  const [imageLoader, setImageLoader] = useState(false);

  const initialValues = {
    title: '',
    description: '',
    button_text: '',
    redirect_url: '',
    guide_image: '',
    status: '1',
  };

  const handleSubmit = async (values) => {
    if (imageLoader) {
      swal({
        title: 'Wait a few seconds',
        text: 'Image is still uploading.',
        icon: 'error',
        dangerMode: true,
      });
      return;
    }

    const requiredFields = ['title', 'description', 'button_text', 'redirect_url'];
    const fieldLabels = {
      title: 'Title',
      description: 'Description',
      button_text: 'Button Text',
      redirect_url: 'Redirect Url',
    };

    const emptyFields = requiredFields
      .filter((field) => !values[field]?.trim())
      .map((field) => fieldLabels[field]);

    if (emptyFields.length > 0) {
      swal({
        title: 'Required fields are empty',
        text: emptyFields.join(', '),
        icon: 'error',
        dangerMode: true,
      });
      return;
    }

    const finalGuideImage = datas?.guide_image_value || values.guide_image?.trim() || '';

    const payload = {
      title: values.title.trim(),
      description: values.description.trim(),
      button_text: values.button_text.trim(),
      redirect_url: values.redirect_url.trim(),
      guide_image: finalGuideImage,
      status: String(values.status) === '1' ? 1 : 0,
    };

    await apiMethod('guidance', payload);
  };

  useEffect(() => {
    if (res.error) {
      toast.error(res?.error?.response?.data?.message ?? 'Failed to create guidance');
    }
  }, [res.error]);

  useEffect(() => {
    if (res.data) {
      const { status, message } = res.data;

      if (status === 'false' || status === false) {
        toast.error(message);
        return;
      }

      toast.success(message ?? 'Guidance created successfully');
      navigate('/guidance');
    }
  }, [navigate, res.data]);

  return (
    <section className="PushNotificationCreate pr-10 max-lg:pr-6">
      <Link to="/guidance" className="back flex items-center mb-5 gap-2">
        <img src={back} className="w-[2rem]" alt="" />
        <span className="text-[1.4rem] font-MluvkaBold">
          Create Guidance
        </span>
      </Link>

      <div className="relative flex items-start gap-3">
        <div className="bg-white rounded-xl w-full mx-auto relative">
          <Formik initialValues={initialValues} onSubmit={handleSubmit}>
            <Form name="guidanceCreateForm">
              <div className="bg-[#EFF4FD] p-6 rounded-3xl mb-3 max-lg:p-3">
                <div className="grid grid-cols-2 gap-4 max-lg:grid-cols-1">
                  <FormControl
                    name="title"
                    label="Title"
                    placeholder="Enter title"
                    className="outline-none w-full h-[3rem] px-5 rounded-xl"
                    control="input2"
                  />
                  <FormControl
                    name="button_text"
                    label="Button Text"
                    placeholder="Enter button text"
                    className="outline-none w-full h-[3rem] px-5 rounded-xl"
                    control="input2"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4 mt-4 max-lg:grid-cols-1">
                  <FormControl
                    name="redirect_url"
                    label="Redirect Url"
                    placeholder="Enter redirect url"
                    className="outline-none w-full h-[3rem] px-5 rounded-xl"
                    control="input2"
                  />

                  <div className="inputBox w-full mt-3">
                    <label className="mb-2 block text-[#7D8CA7] text-[.8rem]">
                      Status
                    </label>
                    <Field
                      as="select"
                      name="status"
                      className="outline-none w-full h-[3rem] px-5 rounded-xl"
                    >
                      <option value="1">Active</option>
                      <option value="0">Inactive</option>
                    </Field>
                  </div>
                </div>

                <div className="mt-4">
                  <FormControl
                    name="guide_image"
                    label="Guidance Media Url"
                    placeholder="Paste image/video url if needed"
                    className="outline-none w-full h-[3rem] px-5 rounded-xl"
                    control="input2"
                  />
                </div>

                <div className="mt-4">
                  <label className="mb-2 block text-[#7D8CA7] text-[.8rem]">
                    Upload Image
                  </label>
                  <OneImageUpload
                    changeImage={setImageLoader}
                    MainImage={datas?.guide_image}
                    Update={setDatas}
                    sec_value={'guide_image_value'}
                    sec_image={'guide_image'}
                    folder_name={'guide-images'}
                    page_type={'guidance'}
                  />
                  <p className='text-[#7D8CA7] text-[.75rem] mt-2'>
                    Image upload supported here. For video guidance, paste the media URL above.
                  </p>
                </div>

                <div className="mt-4">
                  <FormControl
                    name="description"
                    label="Description"
                    placeholder="Enter description"
                    className="outline-none w-full h-[8rem] px-5 py-3 rounded-xl resize-none"
                    control="textarea2"
                  />
                </div>
              </div>

              {canAccessGuideAction(permission, 'Guide Add') && (
                <SubmitButton
                  props={{
                    class:
                      'btn bg-secondary text-white px-12 ml-auto uppercase mb-3 py-3 rounded-full w-100 block mt-5 submit hover:bg-primary transition-all duration-300',
                    text: 'Submit',
                  }}
                  buttonLoading={res.isLoading}
                />
              )}
            </Form>
          </Formik>
        </div>
      </div>
    </section>
  );
};

export default GuidanceCreate;
